'use client'

import { type FaqsQuery } from '@/generated/graphql'
import Link from 'next/link'
import { useState } from 'react'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'

type BrandCode = 'VANISHING_INC_US' | 'VANISHING_INC_UK'

export const FaqsClient = ({
  categories,
  brandCode,
  faqs,
}: FaqsClientProps) => {
  const [searchTerm, setSearchTerm] = useState('')

  const getFaqsByCategory = (categoryKey: string) => {
    const faqsForCategory = faqs?.filter(faq => faq.faqType === categoryKey) || []

    if (categoryKey !== 'shipping') {
      return faqsForCategory
    }

    // Temporary workaround: filter by slug suffix until DB brandId is fixed.
    return faqsForCategory.filter(faq => {
      const urlStub = faq.urlStub?.toLowerCase()
      if (!urlStub) return true

      const isUkSlug = urlStub.endsWith('-uk')
      const isUsaSlug = urlStub.endsWith('-usa')
      if (!isUkSlug && !isUsaSlug) return true

      return brandCode === 'VANISHING_INC_UK' ? isUkSlug : isUsaSlug
    })
  }

  const filteredCategories = categories.filter(category => {
    const filteredFaqs = getFaqsByCategory(category.key)
    if (!filteredFaqs || filteredFaqs.length === 0) return false

    if (!searchTerm) return true

    return filteredFaqs.some(
      faq =>
        faq.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })

  const getFilteredFaqs = (categoryKey: string) => {
    const filteredFaqs = getFaqsByCategory(categoryKey)
    if (!searchTerm) return filteredFaqs

    return filteredFaqs.filter(faq =>
      faq.title?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  const clearSearch = () => {
    setSearchTerm('')
  }

  return (
    <>
      <div className="mb-4 bg-gray-50 rounded-lg p-4">
        <div className="relative max-w-lg mx-auto">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search our FAQs..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="block w-full pl-12 pr-12 py-4 text-lg border-2 border-gray-200 rounded-2xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md focus:shadow-md"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute inset-y-0 right-0 pr-4 flex items-center hover:bg-gray-50 rounded-r-2xl transition-colors duration-200"
            >
              <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        {filteredCategories.map(category => {
          const filteredFaqs = getFilteredFaqs(category.key)

          return (
            <div
              className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group"
              key={category.key}
            >
              <div className="pb-4">
                <h3 className="font-bold text-2xl text-gray-900 mt-4 px-4">
                  {category.title}
                </h3>
                <div className="text-sm text-gray-500 border-b border-gray-100 pb-2 px-4">
                  {filteredFaqs.length} question
                  {filteredFaqs.length !== 1 ? 's' : ''}
                </div>
                <div className="space-y-0">
                  {filteredFaqs.map(faq => (
                    <div
                      key={faq.id}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <Link
                        href={`/faqs/${faq.urlStub}`}
                        className="block py-3 px-4 text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200 group/link"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium pr-2 flex-1 min-w-0">
                            {faq.title}
                          </span>
                          <svg
                            className="w-4 h-4 text-gray-400 group-hover/link:text-vi-red group-hover/link:translate-x-1 transition-all duration-200 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {filteredCategories.length === 0 && (
        <div className="text-center py-16">
          <h3 className="text-2xl font-bold text-gray-700 mb-4">
            No FAQs found
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            We couldn&apos;t find any FAQs matching your search. Try different
            keywords or browse all categories.
          </p>
          <button
            onClick={clearSearch}
            className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 font-medium"
          >
            <XMarkIcon className="w-4 h-4 mr-2" />
            Clear search
          </button>
        </div>
      )}
    </>
  )
}

interface FaqsClientProps {
  categories: Array<{ title: string; key: string }>
  brandCode: BrandCode
  faqs: FaqsQuery['faqs']
}
